import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@cr/shared';
import { getTelegramUser } from './telegram';

const STORAGE_KEY = 'cr:locale';

const resources = {
  ru: {
    translation: {
      nav: {
        home: 'Главная',
        catalog: 'Каталог',
        favorites: 'Избранное',
        cart: 'Корзина',
        orders: 'Заказы',
        profile: 'Профиль',
      },
      home: {
        brand: 'Аренда одежды',
        greeting: 'Добро пожаловать',
        searchPlaceholder: 'Что будем арендовать?',
        categories: 'Категории',
        popular: 'Популярное',
        newArrivals: 'Новые поступления',
        promos: 'Акции',
        showAll: 'Все',
        bannerAction: 'Смотреть',
        empty: 'Каталог пока пуст',
      },
      search: {
        title: 'Поиск',
        recent: 'Вы искали',
        clearRecent: 'Очистить',
        hintTitle: 'Что ищем?',
        hint: 'Введите название или бренд — покажем подходящее',
        tryAnother: 'Попробуйте другой запрос',
      },
      profile: {
        title: 'Профиль',
        guest: 'Гость',
        noPhone: 'Телефон не привязан',
        verified: 'MyID пройден',
        addresses: 'Адреса',
        noAddresses: 'Сохранённых адресов пока нет — добавите при оформлении заказа',
        defaultAddress: 'Основной',
        notifications: 'Уведомления',
        notificationsHint: 'Об оплате, штрафах и MyID сообщаем всегда — от них отписаться нельзя',
        event: {
          RENTAL_STARTED: 'Начало аренды',
          RETURN_REMINDER: 'Напоминание о возврате',
          ITEM_RETURNED: 'Вещь принята',
          INSPECTION_STARTED: 'Начало проверки',
          ORDER_COMPLETED: 'Заказ завершён',
          DEPOSIT_REFUNDED: 'Возврат залога',
        },
        language: 'Язык',
        logout: 'Выйти',
        offerVersion: 'Редакция {{version}}',
        noOffer: 'Оферта пока не опубликована',
      },
      catalog: {
        title: 'Каталог',
        filters: 'Фильтры',
        allCategories: 'Все категории',
        size: 'Размер',
        color: 'Цвет',
        price: 'Цена за сутки',
        from: 'от',
        to: 'до',
        sort: 'Сортировка',
        sortNew: 'Сначала новые',
        sortPopular: 'Популярные',
        sortPriceAsc: 'Сначала дешевле',
        sortPriceDesc: 'Сначала дороже',
        apply: 'Показать',
        loadMore: 'Показать ещё',
        reset: 'Сбросить',
        nothingFound: 'Ничего не нашлось',
        found: 'Найдено: {{count}}',
        showAllInCategory: 'Показать все товары',
        itemsCount_one: '{{count}} вещь',
        itemsCount_few: '{{count}} вещи',
        itemsCount_many: '{{count}} вещей',
      },
      product: {
        perDay: 'сут',
        new: 'Новинка',
        fabric: 'Состав',
        brand: 'Бренд',
        popular: 'Хит',
        deposit: 'Залог',
        noDeposit: 'Без залога',
        size: 'Размер',
        color: 'Цвет',
        accessories: 'Дополнительно',
        period: 'Период аренды',
        pickDates: 'Выберите даты',
        selectStart: 'Выберите дату начала',
        selectEnd: 'Выберите дату окончания',
        days_one: '{{count}} день',
        days_few: '{{count}} дня',
        days_many: '{{count}} дней',
        total: 'Итого',
        rent: 'Аренда',
        addToCart: 'В корзину',
        added: 'Добавлено в корзину',
        unavailable: 'На эти даты занято',
        chooseVariant: 'Выберите размер и цвет',
        minDays: 'Минимум {{count}} дн.',
        maxDays: 'Максимум {{count}} дн.',
        description: 'Описание',
        buffer: 'После возврата вещь уходит на чистку — эти дни недоступны следующему арендатору',
      },
      cart: {
        title: 'Корзина',
        empty: 'Корзина пуста',
        emptyHint: 'Загляните в каталог — там есть что примерить',
        goToCatalog: 'В каталог',
        rent: 'Аренда',
        accessories: 'Аксессуары',
        deposit: 'Залог',
        depositHint: 'Возвращается после проверки вещи',
        total: 'К оплате',
        checkout: 'Оформить заказ',
        remove: 'Удалить',
        unavailableItem: 'Даты заняты — измените период',
        checkoutSoon: 'Оформление заказа появится в следующем обновлении',
      },
      checkout: {
        title: 'Оформление',
        howToGet: 'Как получить',
        delivery: 'Доставка',
        pickup: 'Самовывоз',
        address: 'Адрес доставки',
        addAddress: 'Добавить адрес',
        addressLine: 'Улица, дом, квартира',
        addressLabel: 'Название (Дом, Работа)',
        addressComment: 'Подъезд, этаж, домофон',
        coords: 'Координаты',
        coordsHint: 'Пока вводятся вручную: широта и долгота. Карта появится с ключом Yandex',
        save: 'Сохранить',
        pickupPoint: 'Пункт выдачи',
        payment: 'Оплата',
        payClick: 'Click',
        payPayme: 'Payme',
        payCash: 'Наличными при получении',
        cashHint: 'Залог и оплата — наличными на пункте выдачи, понадобится паспорт',
        promo: 'Промокод',
        promoApply: 'Применить',
        promoApplied: 'Промокод применён',
        comment: 'Комментарий к заказу',
        offer: 'Я ознакомился и согласен с условиями публичной оферты',
        offerRead: 'Читать оферту',
        offerRequired: 'Без согласия с офертой заказ не оформить',
        delivery_cost: 'Доставка',
        deliveryFree: 'бесплатно',
        submit: 'Оформить заказ',
        submitting: 'Оформляем…',
        deliveryUnavailable: 'Доставка по этому адресу недоступна',
      },
      orders: {
        title: 'Мои заказы',
        empty: 'Заказов пока нет',
        number: 'Заказ',
        created: 'Оформлен',
        period: 'Период аренды',
        items: 'Состав',
        payment: 'Оплата',
        delivery: 'Получение',
        total: 'Итого',
        history: 'История',
        pay: 'Оплатить',
        fine: 'Штраф за повреждение',
        fineHint: 'Удерживается из залога, остаток возвращается',
        lateFee_one: 'Пеня за просрочку, {{count}} день',
        lateFee_few: 'Пеня за просрочку, {{count}} дня',
        lateFee_many: 'Пеня за просрочку, {{count}} дней',
        cancel: 'Отменить заказ',
        status: {
          NEW: 'Новый',
          AWAITING_PAYMENT: 'Ожидает оплаты',
          PAID: 'Оплачен',
          AWAITING_ADMIN_CONFIRMATION: 'Ожидает подтверждения',
          AWAITING_MYID: 'Нужна верификация MyID',
          VERIFIED: 'Верификация пройдена',
          PREPARING: 'Подготавливается',
          READY_FOR_PICKUP: 'Готов к выдаче',
          HANDED_TO_DELIVERY: 'Передан в доставку',
          DELIVERED: 'Доставлен',
          RENTAL_ACTIVE: 'Аренда активна',
          AWAITING_RETURN: 'Ожидает возврата',
          RETURNED: 'Возвращён',
          WAREHOUSE_INSPECTION: 'Проверяется складом',
          DAMAGE_DETECTED: 'Обнаружено повреждение',
          FINE_ISSUED: 'Начислен штраф',
          COMPLETED: 'Завершён',
          CANCELLED: 'Отменён',
        },
      },
      myid: {
        title: 'Подтверждение личности',
        hint: 'Перед выдачей нужно подтвердить личность через MyID — это требование при аренде под залог',
        start: 'Пройти верификацию',
        continue: 'Продолжить верификацию',
        waiting: 'Ждём ответ от MyID. Экран обновится сам',
        failed: 'Верификация не пройдена',
        expired: 'Время сессии истекло — начните заново',
        attemptsLeft_one: 'Осталась {{count}} попытка',
        attemptsLeft_few: 'Осталось {{count}} попытки',
        attemptsLeft_many: 'Осталось {{count}} попыток',
      },
      payment: {
        history: 'Платежи',
        opensExternally: 'Оплата откроется во внешнем приложении. Заказ обновится сам',
        method: { CLICK: 'Click', PAYME: 'Payme', CASH: 'Наличными при получении' },
        status: {
          PENDING: 'Ожидает',
          PAID: 'Оплачен',
          CANCELLED: 'Отменён',
          REFUNDED: 'Возвращён',
          FAILED: 'Не прошёл',
        },
      },
      favorites: { title: 'Избранное', empty: 'Пока пусто', hint: 'Нажимайте ♥ на товарах' },
      common: {
        loading: 'Загрузка…',
        error: 'Что-то пошло не так',
        retry: 'Повторить',
        back: 'Назад',
        currency: 'сум',
        outsideTelegram: 'Откройте приложение через Telegram, чтобы бронировать вещи',
      },
    },
  },
  uz: {
    translation: {
      nav: {
        home: 'Asosiy',
        catalog: 'Katalog',
        favorites: 'Saralangan',
        cart: 'Savat',
        orders: 'Buyurtmalar',
        profile: 'Profil',
      },
      home: {
        brand: 'Kiyim ijarasi',
        greeting: 'Xush kelibsiz',
        searchPlaceholder: 'Nima ijaraga olamiz?',
        categories: 'Turkumlar',
        popular: 'Ommabop',
        newArrivals: 'Yangi kelganlar',
        promos: 'Aksiyalar',
        showAll: 'Hammasi',
        bannerAction: 'Ko‘rish',
        empty: 'Katalog hozircha bo‘sh',
      },
      search: {
        title: 'Qidiruv',
        recent: 'Siz qidirgan',
        clearRecent: 'Tozalash',
        hintTitle: 'Nima qidiryapsiz?',
        hint: 'Nomi yoki brendini yozing — mosini ko‘rsatamiz',
        tryAnother: 'Boshqa so‘rov bilan urinib ko‘ring',
      },
      profile: {
        title: 'Profil',
        guest: 'Mehmon',
        noPhone: 'Telefon biriktirilmagan',
        verified: 'MyID tasdiqlangan',
        addresses: 'Manzillar',
        noAddresses: 'Saqlangan manzil yo‘q — buyurtma berishda qo‘shasiz',
        defaultAddress: 'Asosiy',
        notifications: 'Bildirishnomalar',
        notificationsHint: 'To‘lov, jarima va MyID haqida doim xabar beramiz',
        event: {
          RENTAL_STARTED: 'Ijara boshlandi',
          RETURN_REMINDER: 'Qaytarish eslatmasi',
          ITEM_RETURNED: 'Buyum qabul qilindi',
          INSPECTION_STARTED: 'Tekshiruv boshlandi',
          ORDER_COMPLETED: 'Buyurtma yakunlandi',
          DEPOSIT_REFUNDED: 'Garov qaytarildi',
        },
        language: 'Til',
        logout: 'Chiqish',
        offerVersion: '{{version}}-tahrir',
        noOffer: 'Oferta hali chop etilmagan',
      },
      catalog: {
        title: 'Katalog',
        filters: 'Filtrlar',
        allCategories: 'Barcha turkumlar',
        size: 'O‘lcham',
        color: 'Rang',
        price: 'Kunlik narx',
        from: 'dan',
        to: 'gacha',
        sort: 'Saralash',
        sortNew: 'Avval yangilari',
        sortPopular: 'Ommabop',
        sortPriceAsc: 'Avval arzoni',
        sortPriceDesc: 'Avval qimmati',
        apply: 'Ko‘rsatish',
        loadMore: 'Yana ko‘rsatish',
        reset: 'Tozalash',
        nothingFound: 'Hech narsa topilmadi',
        found: 'Topildi: {{count}}',
        showAllInCategory: 'Barcha mahsulotlar',
        itemsCount_one: '{{count}} ta kiyim',
        itemsCount_other: '{{count}} ta kiyim',
      },
      product: {
        perDay: 'kun',
        new: 'Yangi',
        fabric: 'Tarkibi',
        brand: 'Brend',
        popular: 'Ommabop',
        deposit: 'Garov',
        noDeposit: 'Garovsiz',
        size: 'O‘lcham',
        color: 'Rang',
        accessories: 'Qo‘shimcha',
        period: 'Ijara muddati',
        pickDates: 'Sanalarni tanlang',
        selectStart: 'Boshlanish sanasi',
        selectEnd: 'Tugash sanasi',
        days_one: '{{count}} kun',
        days_few: '{{count}} kun',
        days_many: '{{count}} kun',
        total: 'Jami',
        rent: 'Ijara',
        addToCart: 'Savatga',
        added: 'Savatga qo‘shildi',
        unavailable: 'Bu sanalar band',
        chooseVariant: 'O‘lcham va rangni tanlang',
        minDays: 'Kamida {{count}} kun',
        maxDays: 'Ko‘pi bilan {{count}} kun',
        description: 'Tavsif',
        buffer: 'Qaytgach buyum tozalanadi — bu kunlar band bo‘ladi',
      },
      cart: {
        title: 'Savat',
        empty: 'Savat bo‘sh',
        emptyHint: 'Katalogga qarang',
        goToCatalog: 'Katalogga',
        rent: 'Ijara',
        accessories: 'Aksessuarlar',
        deposit: 'Garov',
        depositHint: 'Tekshiruvdan so‘ng qaytariladi',
        total: 'To‘lovga',
        checkout: 'Buyurtma berish',
        remove: 'O‘chirish',
        unavailableItem: 'Sanalar band — muddatni o‘zgartiring',
        checkoutSoon: 'Buyurtma berish keyingi yangilanishda',
      },
      checkout: {
        title: 'Rasmiylashtirish',
        howToGet: 'Qanday olasiz',
        delivery: 'Yetkazib berish',
        pickup: 'Olib ketish',
        address: 'Yetkazish manzili',
        addAddress: 'Manzil qo‘shish',
        addressLine: 'Ko‘cha, uy, xonadon',
        addressLabel: 'Nomi (Uy, Ish)',
        addressComment: 'Kirish, qavat, domofon',
        coords: 'Koordinatalar',
        coordsHint: 'Hozircha qo‘lda kiritiladi. Xarita Yandex kaliti bilan paydo bo‘ladi',
        save: 'Saqlash',
        pickupPoint: 'Topshirish punkti',
        payment: 'To‘lov',
        payClick: 'Click',
        payPayme: 'Payme',
        payCash: 'Olishda naqd',
        cashHint: 'To‘lov va garov punktda naqd, pasport kerak bo‘ladi',
        promo: 'Promokod',
        promoApply: 'Qo‘llash',
        promoApplied: 'Promokod qo‘llandi',
        comment: 'Buyurtmaga izoh',
        offer: 'Ommaviy oferta shartlari bilan tanishdim va roziman',
        offerRead: 'Ofertani o‘qish',
        offerRequired: 'Ofertaga rozilik bo‘lmasa buyurtma berilmaydi',
        delivery_cost: 'Yetkazib berish',
        deliveryFree: 'bepul',
        submit: 'Buyurtma berish',
        submitting: 'Yuborilmoqda…',
        deliveryUnavailable: 'Bu manzilga yetkazib berilmaydi',
      },
      orders: {
        title: 'Buyurtmalarim',
        empty: 'Hozircha buyurtma yo‘q',
        number: 'Buyurtma',
        created: 'Berilgan',
        period: 'Ijara muddati',
        items: 'Tarkibi',
        payment: 'To‘lov',
        delivery: 'Olish',
        total: 'Jami',
        history: 'Tarix',
        pay: 'To‘lash',
        fine: 'Shikast uchun jarima',
        fineHint: 'Garovdan ushlab qolinadi, qoldig‘i qaytariladi',
        lateFee_one: 'Kechikish jarimasi, {{count}} kun',
        lateFee_other: 'Kechikish jarimasi, {{count}} kun',
        cancel: 'Buyurtmani bekor qilish',
        status: {
          NEW: 'Yangi',
          AWAITING_PAYMENT: 'To‘lov kutilmoqda',
          PAID: 'To‘langan',
          AWAITING_ADMIN_CONFIRMATION: 'Tasdiqlash kutilmoqda',
          AWAITING_MYID: 'MyID tekshiruvi kerak',
          VERIFIED: 'Tekshiruv o‘tdi',
          PREPARING: 'Tayyorlanmoqda',
          READY_FOR_PICKUP: 'Olishga tayyor',
          HANDED_TO_DELIVERY: 'Yetkazishga berildi',
          DELIVERED: 'Yetkazildi',
          RENTAL_ACTIVE: 'Ijara faol',
          AWAITING_RETURN: 'Qaytarish kutilmoqda',
          RETURNED: 'Qaytarildi',
          WAREHOUSE_INSPECTION: 'Omborda tekshirilmoqda',
          DAMAGE_DETECTED: 'Shikast aniqlandi',
          FINE_ISSUED: 'Jarima hisoblandi',
          COMPLETED: 'Yakunlandi',
          CANCELLED: 'Bekor qilindi',
        },
      },
      myid: {
        title: 'Shaxsni tasdiqlash',
        hint: 'Buyumni olishdan oldin MyID orqali shaxsni tasdiqlash kerak',
        start: 'Tasdiqlashni boshlash',
        continue: 'Tasdiqlashni davom ettirish',
        waiting: 'MyID javobi kutilmoqda. Ekran o‘zi yangilanadi',
        failed: 'Tasdiqlash o‘tmadi',
        expired: 'Sessiya muddati tugadi — qaytadan boshlang',
        attemptsLeft_one: '{{count}} urinish qoldi',
        attemptsLeft_other: '{{count}} urinish qoldi',
      },
      payment: {
        history: 'To‘lovlar',
        opensExternally: 'To‘lov tashqi ilovada ochiladi. Buyurtma o‘zi yangilanadi',
        method: { CLICK: 'Click', PAYME: 'Payme', CASH: 'Olishda naqd' },
        status: {
          PENDING: 'Kutilmoqda',
          PAID: 'To‘langan',
          CANCELLED: 'Bekor qilingan',
          REFUNDED: 'Qaytarilgan',
          FAILED: 'O‘tmadi',
        },
      },
      favorites: { title: 'Saralangan', empty: 'Hozircha bo‘sh', hint: 'Mahsulotlarda ♥ bosing' },
      common: {
        loading: 'Yuklanmoqda…',
        error: 'Xatolik yuz berdi',
        retry: 'Qayta urinish',
        back: 'Orqaga',
        currency: 'so‘m',
        outsideTelegram: 'Ijara uchun ilovani Telegram orqali oching',
      },
    },
  },
  en: {
    translation: {
      nav: {
        home: 'Home',
        catalog: 'Catalog',
        favorites: 'Favorites',
        cart: 'Cart',
        orders: 'Orders',
        profile: 'Profile',
      },
      home: {
        brand: 'Clothes rental',
        greeting: 'Welcome',
        searchPlaceholder: 'What are we renting?',
        categories: 'Categories',
        popular: 'Popular',
        newArrivals: 'New arrivals',
        promos: 'Offers',
        showAll: 'See all',
        bannerAction: 'View',
        empty: 'Catalog is empty',
      },
      search: {
        title: 'Search',
        recent: 'Recent searches',
        clearRecent: 'Clear',
        hintTitle: 'What are you looking for?',
        hint: 'Type a name or a brand — we will show the matches',
        tryAnother: 'Try another query',
      },
      profile: {
        title: 'Profile',
        guest: 'Guest',
        noPhone: 'No phone linked',
        verified: 'MyID verified',
        addresses: 'Addresses',
        noAddresses: 'No saved addresses yet — you will add one at checkout',
        defaultAddress: 'Default',
        notifications: 'Notifications',
        notificationsHint: 'Payment, fines and MyID are always sent — you cannot opt out',
        event: {
          RENTAL_STARTED: 'Rental started',
          RETURN_REMINDER: 'Return reminder',
          ITEM_RETURNED: 'Item received',
          INSPECTION_STARTED: 'Inspection started',
          ORDER_COMPLETED: 'Order completed',
          DEPOSIT_REFUNDED: 'Deposit refunded',
        },
        language: 'Language',
        logout: 'Log out',
        offerVersion: 'Revision {{version}}',
        noOffer: 'The offer has not been published yet',
      },
      catalog: {
        title: 'Catalog',
        filters: 'Filters',
        allCategories: 'All categories',
        size: 'Size',
        color: 'Color',
        price: 'Price per day',
        from: 'from',
        to: 'to',
        sort: 'Sort',
        sortNew: 'Newest first',
        sortPopular: 'Popular',
        sortPriceAsc: 'Cheapest first',
        sortPriceDesc: 'Most expensive first',
        apply: 'Show',
        loadMore: 'Show more',
        reset: 'Reset',
        nothingFound: 'Nothing found',
        found: 'Found: {{count}}',
        showAllInCategory: 'Show all items',
        itemsCount_one: '{{count}} item',
        itemsCount_other: '{{count}} items',
      },
      product: {
        perDay: 'day',
        new: 'New',
        fabric: 'Fabric',
        brand: 'Brand',
        popular: 'Popular',
        deposit: 'Deposit',
        noDeposit: 'No deposit',
        size: 'Size',
        color: 'Color',
        accessories: 'Add-ons',
        period: 'Rental period',
        pickDates: 'Pick dates',
        selectStart: 'Select start date',
        selectEnd: 'Select end date',
        days_one: '{{count}} day',
        days_other: '{{count}} days',
        total: 'Total',
        rent: 'Rent',
        addToCart: 'Add to cart',
        added: 'Added to cart',
        unavailable: 'Unavailable on these dates',
        chooseVariant: 'Choose size and color',
        minDays: 'Minimum {{count}} days',
        maxDays: 'Maximum {{count}} days',
        description: 'Description',
        buffer: 'After return the item goes to cleaning — those days are blocked',
      },
      cart: {
        title: 'Cart',
        empty: 'Cart is empty',
        emptyHint: 'Take a look at the catalog',
        goToCatalog: 'To catalog',
        rent: 'Rent',
        accessories: 'Add-ons',
        deposit: 'Deposit',
        depositHint: 'Refunded after inspection',
        total: 'To pay',
        checkout: 'Checkout',
        remove: 'Remove',
        unavailableItem: 'Dates are taken — change the period',
        checkoutSoon: 'Checkout is coming in the next update',
      },
      checkout: {
        title: 'Checkout',
        howToGet: 'How to receive',
        delivery: 'Delivery',
        pickup: 'Pickup',
        address: 'Delivery address',
        addAddress: 'Add address',
        addressLine: 'Street, building, apartment',
        addressLabel: 'Label (Home, Work)',
        addressComment: 'Entrance, floor, intercom',
        coords: 'Coordinates',
        coordsHint: 'Entered manually for now. The map arrives with the Yandex key',
        save: 'Save',
        pickupPoint: 'Pickup point',
        payment: 'Payment',
        payClick: 'Click',
        payPayme: 'Payme',
        payCash: 'Cash on pickup',
        cashHint: 'Payment and deposit in cash at the pickup point, bring your passport',
        promo: 'Promo code',
        promoApply: 'Apply',
        promoApplied: 'Promo code applied',
        comment: 'Order comment',
        offer: 'I have read and agree to the public offer',
        offerRead: 'Read the offer',
        offerRequired: 'The order cannot be placed without accepting the offer',
        delivery_cost: 'Delivery',
        deliveryFree: 'free',
        submit: 'Place order',
        submitting: 'Placing…',
        deliveryUnavailable: 'Delivery is not available to this address',
      },
      orders: {
        title: 'My orders',
        empty: 'No orders yet',
        number: 'Order',
        created: 'Placed',
        period: 'Rental period',
        items: 'Items',
        payment: 'Payment',
        delivery: 'Receiving',
        total: 'Total',
        history: 'History',
        pay: 'Pay',
        fine: 'Damage fine',
        fineHint: 'Withheld from the deposit, the rest is refunded',
        lateFee_one: 'Late fee, {{count}} day',
        lateFee_other: 'Late fee, {{count}} days',
        cancel: 'Cancel order',
        status: {
          NEW: 'New',
          AWAITING_PAYMENT: 'Awaiting payment',
          PAID: 'Paid',
          AWAITING_ADMIN_CONFIRMATION: 'Awaiting confirmation',
          AWAITING_MYID: 'MyID verification required',
          VERIFIED: 'Verified',
          PREPARING: 'Preparing',
          READY_FOR_PICKUP: 'Ready for pickup',
          HANDED_TO_DELIVERY: 'Handed to courier',
          DELIVERED: 'Delivered',
          RENTAL_ACTIVE: 'Rental active',
          AWAITING_RETURN: 'Awaiting return',
          RETURNED: 'Returned',
          WAREHOUSE_INSPECTION: 'Inspection',
          DAMAGE_DETECTED: 'Damage detected',
          FINE_ISSUED: 'Fine issued',
          COMPLETED: 'Completed',
          CANCELLED: 'Cancelled',
        },
      },
      myid: {
        title: 'Identity verification',
        hint: 'Before pickup you need to verify your identity via MyID',
        start: 'Start verification',
        continue: 'Continue verification',
        waiting: 'Waiting for MyID. The screen updates on its own',
        failed: 'Verification failed',
        expired: 'The session expired — start again',
        attemptsLeft_one: '{{count}} attempt left',
        attemptsLeft_other: '{{count}} attempts left',
      },
      payment: {
        history: 'Payments',
        opensExternally: 'Payment opens in an external app. The order updates on its own',
        method: { CLICK: 'Click', PAYME: 'Payme', CASH: 'Cash on pickup' },
        status: {
          PENDING: 'Pending',
          PAID: 'Paid',
          CANCELLED: 'Cancelled',
          REFUNDED: 'Refunded',
          FAILED: 'Failed',
        },
      },
      favorites: { title: 'Favorites', empty: 'Nothing here yet', hint: 'Tap ♥ on products' },
      common: {
        loading: 'Loading…',
        error: 'Something went wrong',
        retry: 'Retry',
        back: 'Back',
        currency: 'UZS',
        outsideTelegram: 'Open the app from Telegram to book items',
      },
    },
  },
};

/** Язык берём из сохранённого выбора, иначе из профиля Telegram. */
function detectLocale(): Locale {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && LOCALES.includes(saved as Locale)) return saved as Locale;

  const code = getTelegramUser()?.language_code?.slice(0, 2).toLowerCase();
  return LOCALES.includes(code as Locale) ? (code as Locale) : DEFAULT_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
});

export function setLocale(locale: Locale): void {
  localStorage.setItem(STORAGE_KEY, locale);
  void i18n.changeLanguage(locale);
}

export function currentLocale(): Locale {
  return (i18n.language as Locale) ?? DEFAULT_LOCALE;
}

export default i18n;
